// Tenant resolution — derives tenant from subdomain or custom domain.
// Examples:
//   icaro.zzyon.com        → slug='icaro'
//   torre.cliente.com.br   → custom_domain match
//   localhost / 127.0.0.1  → 'icaro' (dev fallback)

import { supabase } from './http.js';

const DEFAULT_BRANDING = {
  cor_primary: '#122149',
  cor_accent:  '#B9892D',
  app_name:    'Torre ZZYON'
};

// Hardcoded tenants — fallback enquanto SaaS DB não estiver integrado (Fase B)
const FALLBACK_TENANTS = {
  icaro: {
    slug: 'icaro',
    nome: 'Ícaro Express Logistics',
    branding: {
      cor_primary: '#122149',
      cor_accent:  '#B9892D',
      app_name:    'Torre Ícaro Express'
    },
    custom_domain: 'icaro.zzyon.com'
  }
};

let _cached = null;

export async function getCurrentTenant() {
  if (_cached) return _cached;

  const slug = resolveTenantSlug();
  if (!slug) return null;

  // Tenta DB SaaS primeiro
  try {
    const { data } = await supabase
      .schema('saas')
      .from('public_tenant_branding')
      .select('slug,nome,branding,custom_domain')
      .or(`slug.eq.${slug},custom_domain.eq.${location.hostname}`)
      .limit(1)
      .single();

    if (data) {
      _cached = {
        slug: data.slug,
        nome: data.nome,
        branding: { ...DEFAULT_BRANDING, ...(data.branding || {}) },
        custom_domain: data.custom_domain
      };
      return _cached;
    }
  } catch (err) {
    // SaaS DB ausente ou schema saas não exposto via PostgREST → fallback
    console.info('[tenant] saas DB indisponível, usando fallback hardcoded para slug=%s', slug);
  }

  // Fallback hardcoded
  if (FALLBACK_TENANTS[slug]) {
    _cached = FALLBACK_TENANTS[slug];
    return _cached;
  }

  console.warn('[tenant] tenant não encontrado: %s', slug);
  return null;
}

export function resolveTenantSlug() {
  const host = location.hostname;

  // Dev fallback
  if (host === 'localhost' || host === '127.0.0.1' || host.startsWith('192.168.')) {
    return import.meta.env?.VITE_DEFAULT_TENANT_SLUG || 'icaro';
  }

  // *.zzyon.com → first label
  if (host.endsWith('.zzyon.com')) {
    const parts = host.split('.');
    const sub = parts[0];

    // Subdomínios reservados (módulos) → busca tenant ativo armazenado
    const RESERVED = new Set(['www','app','docs','status','api','briefing','ra','cargas','comercial','inbox','platform']);
    if (RESERVED.has(sub)) {
      return new URLSearchParams(location.search).get('tenant')
          || localStorage.getItem('zzy_tenant_slug')
          || import.meta.env?.VITE_DEFAULT_TENANT_SLUG
          || 'icaro';
    }
    return sub;
  }

  // Custom domain — caller resolve via DB
  return import.meta.env?.VITE_DEFAULT_TENANT_SLUG || 'icaro';
}

export function clearTenantCache() {
  _cached = null;
}

export function setActiveTenant(slug) {
  localStorage.setItem('zzy_tenant_slug', slug);
  _cached = null;
}
